import React, { Component } from 'react'
import classnames from 'classnames'
import { toJS } from 'mobx'
import isEqual from 'lodash/isEqual'

import { withSettings } from './withSettings'
import Form from './Form'
import Toggle from './Form/toggle'
import MapSelector from './Form/MapSelector'
import PremiumLabel from './Form/PremiumLabel'
import { isPremium } from '../../../../utils/plans'

const salePriceOptionCategories = [
  [5, 7, 10, 15, 20, 25, 30, 40, 50].map(n => n * 10000),
  [2, 2.5, 3, 4, 5, 6, 7, 8, 9].map(n => n * 100000),
  [5, 6, 7, 8, 9, 10, 15, 20, 25].map(n => n * 100000)
]

const rentPriceOptionCategories = [
  [5, 7.5, 10, 12.5, 15, 17.5, 20, 25, 30].map(n => n * 100),
  [5, 10, 15, 20, 25, 30, 35, 40, 50].map(n => n * 100),
  [2.5, 5, 7, 10, 12, 15, 20, 25, 30].map(n => n * 1000)
]

const PriceOptions = props => (
  <div
    className={classnames('price-options', { selected: props.selected })}
    onClick={() => props.onClick(props.options)}>
    <ul>
      {props.options.map(opt => (
        <li key={String(opt)}>${String(opt).replace(/(\d)(?=(\d{3})+($|\.\d*$))/g, '$1,')}</li>
      ))}
    </ul>
  </div>
)

const PriceOptionsSelector = props => (
  <div className="price-options-selector">
    {props.optionCategories.map(options => (
      <PriceOptions
        key={String(options)}
        options={options}
        selected={isEqual(options, props.selected)}
        onClick={props.onChange} />
    ))}
  </div>
)

@withSettings
export default class DisplaySettings extends Component {

  state = {
    selectedSalePriceOptions: toJS(this.props.getSettingsAt('display.price_options.sale', salePriceOptionCategories[1])),
    selectedRentPriceOptions: toJS(this.props.getSettingsAt('display.price_options.rent', rentPriceOptionCategories[1]))
  }

  handleData = data => {
    const { selectedSalePriceOptions:sale, selectedRentPriceOptions:rent } = this.state
    return this.props.setSettingsAt('display', {
      ...data,
      price_options: { sale, rent }
    })
  }

  render() {
    const display = this.props.getSettingsAt('display')
    const { websites: { current:website } } = this.props

    return (
      <div className="container padded settings">
        <Form onData={this.handleData}>
          <div className="panel padded">
            <h2 className="title">Display Settings</h2>
            <div className="form-section" style={{ background: "#e0e0e0", margin: "-0.2em", padding: "0.2em 0.2em 1em 0.2em" }}>
              <Toggle
                on={display.coronavirusSafeEnabled}
                name="coronavirusSafeEnabled">
                <span>
                  Enable Coronavirus-Safe "Schedule a Tour"
                </span>
                <p className="muted">
                  Change the "Schedule a Tour" call to action to "Schedule a COVID-Safe Tour"
                </p>
              </Toggle>

              <div style={{ marginLeft: "62px"}}>
                <label>COVID NOTE</label>
                <p className="muted">This small additional information will be displayed alongside the "Schedule a Tour" link to help your visitors understand your Coronavirus showings policy.</p>
                <textarea type="text" name="coronavirusSafeMessage" defaultValue={display.coronavirusSafeMessage} />
              </div>
            </div>
            <div className="form-section">
              <Toggle
                disabled={!isPremium(website.plan)}
                on={display.hide_advanced_search}
                name="hide_advanced_search">
                <span>
                  Hide Advanced Search Options
                  <PremiumLabel website={website}/>
                </span>
                <p className="muted">
                  Remove the advanced search section from the default search page
                </p>
              </Toggle>

              <Toggle
                on={display.hide_search_map}
                name="hide_search_map">
                <span>
                  Hide Search Map
                </span>
                <p className="muted">
                  Remove the map display from the default search page
                </p>
              </Toggle>

              <Toggle
                on={display.hide_virtual_tours}
                name="hide_virtual_tours">
                <span>
                  Hide Virtual Tours
                </span>
                <p className="muted">
                  Removes the virtual tour link on listings
                </p>
              </Toggle>

              <Toggle
                on={display.force_mobile_margin}
                name="force_mobile_margin">
                <span>
                  Auto fix margin/padding on mobile
                </span>
                <p className="muted">
                  Forces no margin or padding around app in mobile view
                </p>
              </Toggle>

              <Toggle
                disabled={!isPremium(website.plan)}
                on={display.show_featured_agent_listings}
                name="show_featured_agent_listings">
                <span>
                  Feature agent listings at the top of the search
                  <PremiumLabel website={website}/>
                </span>
                <p className="muted">
                  Show listings for this website's agents at the top of the search results
                </p>
              </Toggle>

              <Toggle
                disabled={!isPremium(website.plan)}
                on={display.show_featured_office_listings}
                name="show_featured_office_listings">
                <span>
                  Feature office listings at the top of the search
                  <PremiumLabel website={website}/>
                </span>
                <p className="muted">
                  Show listings for this website's offices at the top of the search results
                </p>
              </Toggle>

              <Toggle
                on={display.listing_mortgage_calculator}
                name="listing_mortgage_calculator">
                <span>
                  Add Mortgage Calculator to Listing Detail Pages
                </span>
                <p className="muted">
                  Applies to every listing detail page on the site
                </p>
              </Toggle>

               <div className="form-section">
                <label>Property Search Text</label>
                <p className="muted">
                  Customize the default property search text
                </p>
                <input
                  name="placeholder"
                  defaultValue={display.placeholder}
                  maxLength="100"
                />
                <div className="placeholder">
                  <div>
                    <i>Character limit : 100</i>
                  </div>
                  <div>Enter a Location or Keyword</div>
                </div>
              </div>
            </div>

            <div className={classnames('form-section')}>
              <label>Map Style</label>
              <p className="muted">
                Choose the styling for maps on the website
              </p>
              <MapSelector name="map_style" defaultSelected={display.map_style} />
            </div>

            <div className={classnames('form-section', { disabled: !isPremium(website.plan) })}>
              <label>Results Per Page</label>
              <p className="muted">
                The maximum number of listings leads will see before they have to go to
                the next page.
              </p>
              <select name="results_per_page" defaultValue={display.results_per_page} className="big">
                <option value={100}>100</option>
                <option value={60}>60</option>
                <option value={24}>24</option>
                <option value={12}>12</option>
              </select>
            </div>

            <div className={classnames('form-section', { disabled: !isPremium(website.plan) })}>
              <label>Default Min Price Options</label>
              <p className="muted">
                Choose the Price Dropdown option that most closely fits your market.
              </p>
              <div style={{display: 'flex', justifyContent: 'space-around'}}>
                <div>
                  <div style={{textAlign: 'center'}}><strong>Sale Prices</strong></div>
                  <PriceOptionsSelector
                    optionCategories={salePriceOptionCategories}
                    selected={this.state.selectedSalePriceOptions}
                    onChange={opts => this.setState({ selectedSalePriceOptions: opts })} />
                </div>
                <div>
                  <div style={{textAlign: 'center'}}><strong>Rent Prices</strong></div>
                  <PriceOptionsSelector
                    optionCategories={rentPriceOptionCategories}
                    selected={this.state.selectedRentPriceOptions}
                    onChange={opts => this.setState({ selectedRentPriceOptions: opts })} />
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className="big shadowed">Save Changes</button>
        </Form>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// client/components/sections/website/settings/Display.js